import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { cardService } from '../services/api';

interface Card {
  id: string;
  name: string;
  cardNumber: string;
  imageUrl?: string;
  rarity?: string;
  type?: string;
  language?: string;
}

interface User {
  role?: string;
}

const AdminCardUploadPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    fetchCards();

    // Clipboard paste listener
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          if (blob) {
            handleFileSelect(blob);
            // If we pasted an image, prevent default behavior (like pasting into an input)
            // But only if we're not focused on a text input, actually we might want to allow it?
            // Safer to just prevent default if it's an image.
            e.preventDefault();
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, []);

  const checkAdminAccess = () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token) {
      setMessage({ type: 'error', text: t('adminUpload.errors.loginRequired') });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (userStr) {
      try {
        const user: User = JSON.parse(userStr);
        if (user.role !== 'admin') {
          setMessage({ type: 'error', text: t('adminUpload.errors.adminRequired') });
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (error) {
        console.error('Failed to parse user:', error);
        navigate('/login');
      }
    }
  };

  const fetchCards = async () => {
    try {
      setLoading(true);
      setMessage(null);

      // Use cardService instead of direct axios
      // Request all cards by setting a high limit if possible.
      // Based on cardService definition, it accepts params.
      // Request all cards by setting a high limit with no prices included
      const response = await cardService.getCards({ limit: 10000, includePrices: 'false' });

      if (response.data.success && response.data.data) {
        setCards(response.data.data);
        console.log('Cards loaded:', response.data.data.length);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      console.error('Failed to fetch cards:', error);

      let errorMessage = t('adminUpload.errors.loadCardsFailed') + ' ';

      if (error.response) {
        // Server responded with error
        errorMessage += t('adminUpload.errors.serverError', { status: error.response.status }) + ' ';
        if (error.response.status === 401) {
          errorMessage += t('adminUpload.errors.loginAgain');
          setTimeout(() => navigate('/login'), 2000);
        } else if (error.response.status === 403) {
          errorMessage += t('adminUpload.errors.adminRequired');
          setTimeout(() => navigate('/dashboard'), 2000);
        } else if (error.response.status === 500) {
          errorMessage += t('adminUpload.errors.backendError');
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage += t('adminUpload.errors.connectionError');
      } else {
        errorMessage += error.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const processFile = (file: File) => {
    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
      setMessage({ type: 'error', text: t('adminUpload.errors.invalidFileType') });
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: t('adminUpload.errors.fileSizeLimit') });
      return;
    }

    setSelectedFile(file);
    setMessage(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (input: React.ChangeEvent<HTMLInputElement> | File) => {
    let file: File | undefined;

    if (input instanceof File) {
      file = input;
    } else {
      file = input.target.files?.[0];
    }

    if (file) {
      processFile(file);
    }
  };

  const handleManualPaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (item.types && item.types.some(type => type.startsWith('image/'))) {
          // Find the image type (prefer png or jpeg)
          const imageType = item.types.find(type => type.startsWith('image/')) || 'image/png';
          const blob = await item.getType(imageType);
          if (blob) {
            const file = new File([blob], "pasted-image.png", { type: imageType });
            processFile(file);
            return;
          }
        }
      }
      setMessage({ type: 'error', text: "No image found in clipboard" });
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      // Fallback message if permission denied or not supported
      setMessage({ type: 'error', text: "Cannot read clipboard automatically. Please click on the page and press Ctrl+V." });
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCard) {
      setMessage({ type: 'error', text: t('adminUpload.errors.selectCard') });
      return;
    }

    if (!selectedFile) {
      setMessage({ type: 'error', text: t('adminUpload.errors.selectImage') });
      return;
    }

    try {
      setUploading(true);
      setMessage(null);

      const formData = new FormData();
      formData.append('image', selectedFile);

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('token');

      // Note: cardService doesn't have a specific updateImage method exposed yet, 
      // but we can add it or just use axios securely here for now to avoid modifying api.ts too much if not broken.
      // However, we should try to use the configured api instance if possible.
      // But looking at api.ts, there isn't a dedicated card image upload method.
      // Let's stick to axios but use the token correctly as before, or better yet adds it to api.ts?
      // For minimum risk, I will keep axios here but make sure it uses the correct URL.
      // The issue with the original code was loading cards, not necessarily uploading (though maybe related).

      console.log('Uploading to:', `${API_URL}/cards/${selectedCard}/image`);

      await axios.post(
        `${API_URL}/cards/${selectedCard}/image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setMessage({ type: 'success', text: t('adminUpload.success.uploaded') });

      // Reset form
      setSelectedCard('');
      setSelectedFile(null);
      setPreviewUrl(null);

      // Refresh cards to show updated image
      fetchCards();

      // Reset file input
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

    } catch (error: any) {
      console.error('Upload failed:', error);

      let errorMessage = t('adminUpload.errors.uploadFailed') + ' ';

      if (error.response) {
        errorMessage += error.response.data?.message || t('adminUpload.errors.serverError', { status: error.response.status });
        if (error.response.status === 401) {
          errorMessage += ' ' + t('adminUpload.errors.loginAgain');
        } else if (error.response.status === 403) {
          errorMessage += ' ' + t('adminUpload.errors.adminRequired');
        }
      } else if (error.request) {
        errorMessage += t('adminUpload.errors.connectionError');
      } else {
        errorMessage += error.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setUploading(false);
    }
  };

  // Fix: Comparison should be type-safe or handle string/number conversion
  const selectedCardData = cards.find((card) => String(card.id) === String(selectedCard));

  const filteredCards = cards.filter((card) => {
    const matchesSearch = card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.cardNumber.toLowerCase().includes(searchTerm.toLowerCase());

    if (showMissingOnly) {
      return matchesSearch && !card.imageUrl;
    }
    return matchesSearch;
  });

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">{t('adminUpload.pageTitle')}</h1>
        <p className="text-gray-600">
          {t('adminUpload.subtitle')}
        </p>
      </div>

      {/* Message Alert */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
            }`}
        >
          <div className="flex items-center">
            <span className="text-xl mr-2">
              {message.type === 'success' ? '✓' : '⚠'}
            </span>
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Backend Connection Status */}
      {!loading && cards.length === 0 && !message && (
        <div className="mb-6 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-700">
          <div className="flex items-center">
            <span className="text-xl mr-2">ℹ️</span>
            <div>
              <p className="font-medium">Backend Connection Required</p>
              <p className="text-sm mt-1">
                Make sure your backend server is running on <code className="bg-yellow-100 px-1 rounded">http://localhost:3001</code>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {/* Left Column - Upload Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">{t('adminUpload.form.title')}</h2>

          <form onSubmit={handleUpload} className="space-y-6">
            {/* Card Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('adminUpload.form.selectCardLabel')}
              </label>

              <div className="flex flex-col gap-2 mb-2">
                <input
                  type="text"
                  placeholder={t('adminUpload.form.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showMissingOnly}
                    onChange={(e) => setShowMissingOnly(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span>{t('adminUpload.form.showMissingOnly')}</span>
                </label>
              </div>

              <select
                value={selectedCard}
                onChange={(e) => setSelectedCard(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                size={8}
                disabled={loading || cards.length === 0}
              >
                <option value="">{t('adminUpload.form.selectPrompt')}</option>
                {loading ? (
                  <option disabled>{t('adminUpload.form.loadingCards')}</option>
                ) : cards.length === 0 ? (
                  <option disabled>{t('adminUpload.form.noCards')}</option>
                ) : (
                  filteredCards.map((card) => (
                    <option key={card.id} value={card.id}>
                      #{card.cardNumber} {card.language ? `[${card.language}]` : ''} ({card.rarity}) - {card.name} {card.imageUrl ? '✓' : ''}
                    </option>
                  ))
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('adminUpload.form.cardsWithImagesHint')}
              </p>
            </div>

            {/* File Upload */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('adminUpload.form.imageFileLabel')}
                </label>
                <button
                  type="button"
                  onClick={handleManualPaste}
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded border border-gray-300 transition-colors"
                >
                  📋 {t('adminUpload.form.pasteButton')}
                </button>
              </div>
              <input
                id="file-input"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required={!selectedFile}
                disabled={loading || cards.length === 0}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('adminUpload.form.acceptedFormats')}
              </p>
            </div>

            {/* Image Preview */}
            {previewUrl && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('adminUpload.form.previewLabel')}
                </label>
                <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-full h-auto rounded-lg mx-auto"
                    style={{ maxHeight: '300px' }}
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={uploading || !selectedCard || !selectedFile || loading || cards.length === 0}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('adminUpload.form.uploading')}
                </>
              ) : (
                t('adminUpload.form.submitButton')
              )}
            </button>
          </form>
        </div>

        {/* Right Column - Selected Card Info */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">{t('adminUpload.info.title')}</h2>

          {selectedCardData ? (
            <div className="space-y-4">
              {/* Current Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('adminUpload.info.currentImage')}
                </label>
                <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                  {selectedCardData.imageUrl ? (
                    <img
                      src={selectedCardData.imageUrl}
                      alt={selectedCardData.name}
                      className="max-w-full h-auto rounded-lg mx-auto"
                      style={{ maxHeight: '400px' }}
                    />
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <div className="text-6xl mb-2">🎴</div>
                      <p>{t('adminUpload.info.noImage')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Details */}
              <div className="border-t pt-4 space-y-2">
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('adminUpload.info.name')} </span>
                  <p className="text-lg font-bold">{selectedCardData.name}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('adminUpload.info.cardNumber')} </span>
                  <p className="text-gray-900">#{selectedCardData.cardNumber}</p>
                </div>
                {selectedCardData.rarity && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('adminUpload.info.rarity')} </span>
                    <p className="text-gray-900">{selectedCardData.rarity}</p>
                  </div>
                )}
                {selectedCardData.type && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('adminUpload.info.type')} </span>
                    <p className="text-gray-900">{selectedCardData.type}</p>
                  </div>
                )}
                {selectedCardData.language && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('common.language')} </span>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold border ${selectedCardData.language === 'EN'
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}>
                      {selectedCardData.language}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="text-6xl mb-4">📋</div>
              <p>{t('adminUpload.info.selectToView')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Card Statistics */}
      {cards.length > 0 && (
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{cards.length}</div>
            <div className="text-sm text-gray-600">{t('adminUpload.stats.total')}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-3xl font-bold text-green-600">
              {cards.filter((card) => card.imageUrl).length}
            </div>
            <div className="text-sm text-gray-600">{t('adminUpload.stats.withImages')}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-3xl font-bold text-orange-600">
              {cards.filter((card) => !card.imageUrl).length}
            </div>
            <div className="text-sm text-gray-600">{t('adminUpload.stats.missingImages')}</div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="mt-8 text-center">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-600 hover:text-blue-700 font-medium"
        >
          {t('adminUpload.backToDashboard')}
        </button>
      </div>
    </div>
  );
};

export default AdminCardUploadPage;
